import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../../components/Animations'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import {
  DashboardPage,
  EmptyState,
  InlineNotice,
  PageActionRow,
  PageHeaderRow,
  Surface,
  SurfaceHeader,
  SurfaceSubtext,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import Gate from '../../components/Gate'
import { TextInput } from '../../components/Input'
import LoadingBar from '../../components/LoadingBar'
import PageHeader from '../../components/PageHeader'
import { SelectedMethodIcon } from '../../components/PaymentMethodDisplay'
import Radom, {
  ExternalAccount,
  ExternalAccountWithContact,
  Payout,
  WithdrawalAccount,
  WithdrawalRequestWithAccountName
} from '../../state/Radom'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../util/ExternalAccounts'
import { generateRoundFlagIconFromCountryCode } from '../../util/Flags'
import { getMethod, prodNetworks, testNetworks } from '../../util/Managed'
import { useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import RecipientAvatar from '../Send/RecipientAvatar'
import { RecentChip, RecentChipsRow } from '../Send/SendStyles'
import { withdrawalAccountSubtitle } from '../Send/sendUtil'

/**
 * Everyone the organization can pay, in one place: contacts with a saved bank
 * account or wallet, and the organization's own saved accounts. Every row and
 * every account chip is one click from the Send amount step.
 */

const RECENT_LIMIT = 6
const FALLBACK_CONTACT_LIMIT = 50

const SearchField = styled.div`
  max-width: 520px;
  width: 100%;
`

const RowList = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  align-items: center;
  border-top: 1px solid var(--border-soft, #eceff3);
  cursor: pointer;
  display: grid;
  gap: 14px;
  grid-template-columns: 40px minmax(160px, 1fr) minmax(200px, 1.5fr) auto;
  min-width: 0;
  padding: 12px 6px;
  transition: background-color 0.16s ease;

  &:first-child {
    border-top: 0;
  }

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }

  @media (max-width: 760px) {
    grid-template-columns: 40px 1fr auto;

    > :nth-child(3) {
      grid-column: 2 / -1;
    }
  }
`

const RowIcon = styled.div`
  align-items: center;
  display: flex;
  height: 40px;
  justify-content: center;
  width: 40px;
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const RowName = styled.div`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowMeta = styled.div`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const WaysRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
`

const WayChip = styled.button<{ $muted?: boolean }>`
  align-items: center;
  background: var(--surface-hover, #f4f6fa);
  border: 0;
  border-radius: 999px;
  color: ${({ $muted }) => $muted ? 'var(--text-muted, #6b7280)' : 'var(--ink, #20242d)'};
  cursor: ${({ $muted }) => $muted ? 'default' : 'pointer'};
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  gap: 6px;
  line-height: 1.2;
  padding: 4px 10px 4px 6px;
  transition: background-color 0.16s ease;

  &:hover {
    background: ${({ $muted }) => $muted ? 'var(--surface-hover, #f4f6fa)' : 'var(--border, #e9edf4)'};
  }
`

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const sendButtonStyle = { padding: '7px 14px', fontSize: 13 }

interface Person {
  customerId: string
  name: string
  email: string
  accounts: ExternalAccount[]
}

interface RecentRecipient {
  key: string
  name: string
  at: number
  target: string
}

// Send delivers to wallets and EUR IBANs today; other account types are listed
// so the contact is complete, without a shortcut that would dead-end.
const canSendTo = (account: ExternalAccount): boolean =>
  isCryptoExternalAccount(account) || isEurIbanExternalAccount(account)

const accountLabel = (account: ExternalAccount): string => {
  if (account.crypto) {
    const method = getMethod(account.crypto.network)
    return `${method?.name ?? account.crypto.network} wallet`
  }
  if (account.iban) return `${(account.currency || 'EUR').toUpperCase()} IBAN ··${account.iban.last4}`
  if (account.account) return `US account ··${account.account.last4}`
  if (account.gbAccount) return `GBP account ··${account.gbAccount.last4}`
  return `${(account.currency || '').toUpperCase()} ${String(account.accountType)} account`.trim()
}

const accountIcon = (account: ExternalAccount, size: number): ReactElement | null => {
  if (account.crypto) {
    const method = getMethod(account.crypto.network)
    return method ? <SelectedMethodIcon method={method} size={size} disableToolTip /> : null
  }
  const countryCode = account.account ? 'US' : account.gbAccount ? 'GB' : 'EU'
  return generateRoundFlagIconFromCountryCode(countryCode, size)
}

const groupByContact = (rows: ExternalAccountWithContact[]): Person[] => {
  const byContact = new Map<string, Person>()
  for (const row of rows) {
    const account = row.account
    const person = byContact.get(account.customerId) ?? {
      customerId: account.customerId,
      name: row.customerName?.trim() || account.accountOwnerName,
      email: row.customerEmail?.trim() ?? '',
      accounts: []
    }
    person.accounts.push(account)
    byContact.set(account.customerId, person)
  }
  return [...byContact.values()].sort((a, b) => a.name.localeCompare(b.name))
}

const Recipients = observer((): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const canReadRecipients = has('ReadExternalAccount')
  const canReadOwnAccounts = has('ReadWithdrawalAccount')
  const canReadWithdrawals = has('ReadWithdrawal')
  const canReadPayouts = has('ReadPayouts')
  const canPayContacts = has('WritePayouts')

  const [people, setPeople] = useState<Person[]>([])
  const [isLoadingPeople, setIsLoadingPeople] = useState(false)
  const [peopleNotice, setPeopleNotice] = useState('')
  const [ownAccounts, setOwnAccounts] = useState<WithdrawalAccount[]>([])
  const [isLoadingOwnAccounts, setIsLoadingOwnAccounts] = useState(false)
  const [recentPayouts, setRecentPayouts] = useState<Payout[]>([])
  const [recentWithdrawals, setRecentWithdrawals] = useState<WithdrawalRequestWithAccountName[]>([])
  const [query, setQuery] = useState('')

  const networks = testMode ? testNetworks : prodNetworks

  const loadPeople = async (): Promise<void> => {
    setIsLoadingPeople(true)
    setPeopleNotice('')
    try {
      const { data } = await Radom.listOrganizationExternalAccounts()
      setPeople(groupByContact(data))
    } catch (err) {
      // Until the org-wide list is reachable, build it from the most recent
      // contacts, one request each.
      console.error('Failed to load recipients', err)
      try {
        const { data: contacts } = await Radom.listCustomers(0, FALLBACK_CONTACT_LIMIT, networks)
        const results = await Promise.allSettled(contacts.map(async c => await Radom.listExternalAccounts(c.id)))
        const rows: ExternalAccountWithContact[] = []
        results.forEach((result, i) => {
          if (result.status !== 'fulfilled') return
          for (const account of result.value) {
            rows.push({ account, customerName: contacts[i].name, customerEmail: contacts[i].email })
          }
        })
        setPeople(groupByContact(rows))
        setPeopleNotice(`Showing recipients among your ${contacts.length} most recent contacts.`)
      } catch (fallbackErr) {
        console.error('Failed to load contacts', fallbackErr)
        setPeople([])
        setPeopleNotice('Recipients could not be loaded right now.')
      }
    }
    setIsLoadingPeople(false)
  }

  const loadOwnAccounts = async (): Promise<void> => {
    setIsLoadingOwnAccounts(true)
    try {
      const accounts = await Radom.listWithdrawalAccounts()
      setOwnAccounts(accounts.filter(account => {
        if (!account.accountDetails.Crypto) return !testMode
        const method = getMethod(account.accountDetails.Crypto.network, account.accountDetails.Crypto.token)
        return testMode ? !!method.isTestnet : !method.isTestnet
      }))
    } catch (err) {
      console.error('Failed to load saved accounts', err)
      setOwnAccounts([])
    }
    setIsLoadingOwnAccounts(false)
  }

  const loadRecents = async (): Promise<void> => {
    const [withdrawalsResult, payoutsResult] = await Promise.allSettled([
      canReadWithdrawals
        ? Radom.listWithdrawals(0, 8, networks)
        : Promise.resolve({ data: [] as WithdrawalRequestWithAccountName[], total: 0 }),
      canReadPayouts
        ? Radom.listPayouts(0, 8, testMode, [])
        : Promise.resolve({ data: [] as Payout[], total: 0 })
    ])
    setRecentWithdrawals(withdrawalsResult.status === 'fulfilled' ? withdrawalsResult.value.data : [])
    setRecentPayouts(payoutsResult.status === 'fulfilled' ? payoutsResult.value.data : [])
  }

  useEffect(() => {
    if (!permissionsLoaded) return
    if (canReadRecipients) void loadPeople()
    if (canReadOwnAccounts) void loadOwnAccounts()
    void loadRecents()
  }, [permissionsLoaded, testMode])

  // Recent one-tap chips: recent withdrawals map back to saved accounts and
  // recent payout entries map back to contacts, newest first, no duplicates.
  const recents = useMemo((): RecentRecipient[] => {
    const candidates: RecentRecipient[] = []
    for (const withdrawal of recentWithdrawals) {
      const account = ownAccounts.find(a => a.id === withdrawal.withdrawalAccountId)
      if (account) {
        candidates.push({
          key: `own:${account.id}`,
          name: account.name,
          at: new Date(withdrawal.requestedAt).getTime(),
          target: `/send?account=${account.id}`
        })
      }
    }
    for (const payout of recentPayouts) {
      const customerIds = [
        ...(payout.cryptoEntries ?? []).map(entry => entry.customerId),
        ...(payout.fiatEntries ?? []).map(entry => entry.customerId)
      ].filter((id): id is string => !!id)
      for (const customerId of customerIds) {
        const person = people.find(p => p.customerId === customerId)
        if (person) {
          candidates.push({
            key: `person:${customerId}`,
            name: person.name,
            at: new Date(payout.createdAt).getTime(),
            target: `/send?customer=${customerId}`
          })
        }
      }
    }
    const seen = new Set<string>()
    const chips: RecentRecipient[] = []
    for (const candidate of candidates.sort((a, b) => b.at - a.at)) {
      if (seen.has(candidate.key)) continue
      seen.add(candidate.key)
      chips.push(candidate)
    }
    return chips.slice(0, RECENT_LIMIT)
  }, [recentWithdrawals, recentPayouts, ownAccounts, people])

  const term = query.trim().toLowerCase()
  const matches = (...parts: Array<string | undefined>): boolean =>
    !term || parts.some(part => (part ?? '').toLowerCase().includes(term))

  const visiblePeople = people.filter(person => matches(
    person.name,
    person.email,
    ...person.accounts.map(a => `${accountLabel(a)} ${a.accountOwnerName} ${a.crypto?.address ?? ''}`)
  ))
  const visibleOwnAccounts = ownAccounts.filter(account => matches(
    account.name,
    withdrawalAccountSubtitle(account),
    account.accountDetails.Crypto?.address
  ))

  const renderPerson = (person: Person): ReactElement => {
    const sendable = person.accounts.filter(canSendTo)
    const open = (): void => navigate(`/customer/${person.customerId}`)
    return <Row
      key={person.customerId}
      role='link'
      tabIndex={0}
      onClick={open}
      onKeyDown={e => { if (e.key === 'Enter') open() }}>
      <RecipientAvatar name={person.name} size={40} />
      <RowText>
        <RowName>{person.name}</RowName>
        <RowMeta>
          {person.email || `${person.accounts.length} saved account${person.accounts.length === 1 ? '' : 's'}`}
        </RowMeta>
      </RowText>
      <WaysRow onClick={e => e.stopPropagation()}>
        {person.accounts.map(account => {
          const clickable = canPayContacts && canSendTo(account)
          return <WayChip
            key={account.id}
            as={clickable ? 'button' : 'span'}
            type={clickable ? 'button' : undefined}
            $muted={!clickable}
            title={clickable ? `Send funds to this ${accountLabel(account)}` : accountLabel(account)}
            onClick={clickable
              ? () => navigate(`/send?customer=${person.customerId}&account=${account.id}`)
              : undefined}>
            {accountIcon(account, 16)}
            <span>{accountLabel(account)}</span>
          </WayChip>
        })}
      </WaysRow>
      <RowActions onClick={e => e.stopPropagation()}>
        {sendable.length > 0 &&
          <Gate permission='WritePayouts'>
            <SecondaryButton
              style={sendButtonStyle}
              onClick={() => navigate(
                `/send?customer=${person.customerId}${sendable.length === 1 ? `&account=${sendable[0].id}` : ''}`
              )}>
              Send funds
            </SecondaryButton>
          </Gate>}
      </RowActions>
    </Row>
  }

  const renderOwnAccount = (account: WithdrawalAccount): ReactElement => {
    const crypto = account.accountDetails.Crypto
    const method = crypto ? getMethod(crypto.network, crypto.token) : undefined
    const countryCode = account.accountDetails.Fiat?.accountType.us ? 'US' : 'EU'
    return <Row key={account.id} role='link' tabIndex={0} onClick={() => navigate('/accounts')}>
      <RowIcon>
        {crypto && method
          ? <SelectedMethodIcon method={method} size={28} disableToolTip />
          : generateRoundFlagIconFromCountryCode(countryCode, 28)}
      </RowIcon>
      <RowText>
        <RowName>{account.name}</RowName>
        <RowMeta>{withdrawalAccountSubtitle(account)}</RowMeta>
      </RowText>
      <div />
      <RowActions onClick={e => e.stopPropagation()}>
        <Gate anyOf={['WriteWithdrawal', 'WritePayouts']}>
          <SecondaryButton style={sendButtonStyle} onClick={() => navigate(`/send?account=${account.id}`)}>
            Send funds
          </SecondaryButton>
        </Gate>
      </RowActions>
    </Row>
  }

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader title='Recipients' />
        <Gate anyOf={['WritePayouts', 'WriteWithdrawal']}>
          <PageActionRow>
            <PrimaryButton onClick={() => navigate('/send')}>Send funds</PrimaryButton>
          </PageActionRow>
        </Gate>
      </PageHeaderRow>

      <SearchField>
        <TextInput
          placeholder='Search by name, email, IBAN digits or wallet address'
          value={query}
          onChange={e => setQuery((e.target as HTMLInputElement).value)} />
      </SearchField>

      {!term && recents.length > 0 &&
        <Surface $compact>
          <SurfaceHeader>
            <div>
              <SurfaceTitle>Recent</SurfaceTitle>
            </div>
          </SurfaceHeader>
          <RecentChipsRow>
            {recents.map(recent =>
              <RecentChip key={recent.key} type='button' onClick={() => navigate(recent.target)}>
                <RecipientAvatar name={recent.name} size={24} />
                <span>{recent.name}</span>
              </RecentChip>
            )}
          </RecentChipsRow>
        </Surface>}

      <Gate permission='ReadExternalAccount'>
        <Surface>
          <SurfaceHeader>
            <div>
              <SurfaceTitle>People and businesses</SurfaceTitle>
              <SurfaceSubtext>
                Contacts with a saved bank account or wallet. Open a contact to add another way to pay them.
              </SurfaceSubtext>
            </div>
            <SecondaryButton onClick={() => navigate('/customers')}>All contacts</SecondaryButton>
          </SurfaceHeader>
          {peopleNotice && <InlineNotice>{peopleNotice}</InlineNotice>}
          {isLoadingPeople
            ? <LoadingBar style={{ borderRadius: 12, width: '100%', height: 64 }} />
            : visiblePeople.length === 0
              ? <EmptyState>
                {term
                  ? 'No recipients match your search.'
                  : 'No contact has a saved account yet. Add one from a contact’s page, or paste an IBAN or wallet address in Send funds.'}
              </EmptyState>
              : <RowList>{visiblePeople.map(renderPerson)}</RowList>}
        </Surface>
      </Gate>

      <Gate permission='ReadWithdrawalAccount'>
        <Surface>
          <SurfaceHeader>
            <div>
              <SurfaceTitle>Your accounts</SurfaceTitle>
              <SurfaceSubtext>Your own wallets and bank accounts. Sending here books as a withdrawal.</SurfaceSubtext>
            </div>
            <SecondaryButton onClick={() => navigate('/accounts')}>Manage accounts</SecondaryButton>
          </SurfaceHeader>
          {isLoadingOwnAccounts
            ? <LoadingBar style={{ borderRadius: 12, width: '100%', height: 64 }} />
            : visibleOwnAccounts.length === 0
              ? <EmptyState>
                {term ? 'None of your accounts match your search.' : 'No saved accounts yet.'}
              </EmptyState>
              : <RowList>{visibleOwnAccounts.map(renderOwnAccount)}</RowList>}
        </Surface>
      </Gate>
    </DashboardPage>
  </Container>
})

export default Recipients
